/**
 * Invoice Metadata Service
 *
 * Stores supplemental invoice data in Firestore that Helcim doesn't natively
 * support (due date, issued-on, service month, etc.).
 *
 * Collection: `invoiceMetadata`
 * Document ID: String(helcimInvoiceId)
 */

import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../auth/firebase';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface InvoiceMetadata {
  helcimInvoiceId: string;
  dueDate: string;                // YYYY-MM-DD
  issuedOn: string;               // YYYY-MM-DD
  invoiceType: string;            // 'INVOICE' | 'QUOTE' | 'ESTIMATE' | …
  createdAt: string;              // ISO timestamp
  serviceMonth?: string;          // YYYY-MM
}

const COLLECTION = 'invoiceMetadata';

// ─── Write ────────────────────────────────────────────────────────────────────

/**
 * Save (or overwrite) metadata for an invoice.
 * @param invoiceId  Helcim numeric invoiceId (number or string form)
 */
export async function saveInvoiceMetadata(
  invoiceId: string | number,
  meta: Omit<InvoiceMetadata, 'helcimInvoiceId'>,
): Promise<void> {
  const id = String(invoiceId);
  await setDoc(doc(db, COLLECTION, id), {
    helcimInvoiceId: id,
    ...meta,
  });
}

// ─── Read (single) ────────────────────────────────────────────────────────────

/**
 * Fetch metadata for a single invoice. Returns null if not found.
 */
export async function getInvoiceMetadata(
  invoiceId: string | number,
): Promise<InvoiceMetadata | null> {
  const snap = await getDoc(doc(db, COLLECTION, String(invoiceId)));
  return snap.exists() ? (snap.data() as InvoiceMetadata) : null;
}

// ─── Read (batch) ─────────────────────────────────────────────────────────────

/**
 * Fetch metadata for multiple invoices in parallel.
 * Returns a Map keyed by String(invoiceId).
 */
export async function getInvoicesMetadata(
  invoiceIds: (string | number)[],
): Promise<Map<string, InvoiceMetadata>> {
  const map = new Map<string, InvoiceMetadata>();
  await Promise.all(
    invoiceIds.map(async (id) => {
      const snap = await getDoc(doc(db, COLLECTION, String(id)));
      if (snap.exists()) {
        map.set(String(id), snap.data() as InvoiceMetadata);
      }
    }),
  );
  return map;
}
